import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type Dispatch,
  type RefCallback,
  type RefObject,
  type SetStateAction,
} from "react";
import {
  clampPlacement,
  scatterLayout,
  tidyLayout,
  type DeskLayout,
  type DeskMeasure,
  type Placement,
  type Size,
} from "./deskGeometry.ts";
import { useMediaQuery } from "./useMediaQuery.ts";

export type DeskMode = "scatter" | "tidy";

/** Below this width the desk stops scattering and stacks cards in normal flow. */
export const DESK_STACKED_QUERY = "(max-width: 760px)";
const RESIZE_DEBOUNCE_MS = 150;

export interface DeskLayoutApi {
  deskRef: RefCallback<HTMLDivElement>;
  photoRef: RefCallback<HTMLDivElement>;
  registerCard: (id: string) => RefCallback<HTMLElement>;
  isStacked: boolean;
  mode: DeskMode;
  /** False until the first layout has painted, so cards don't fly in from the corner on load. */
  animate: boolean;
  layout: DeskLayout | null;
  toggleMode: () => void;
  getPlacement: (id: string) => Placement | undefined;
  moveCard: (id: string, x: number, y: number) => void;
  markMoved: () => void;
}

interface DeskElements {
  desk: RefObject<HTMLDivElement | null>;
  photo: RefObject<HTMLDivElement | null>;
  deskNode: HTMLDivElement | null;
  deskRef: RefCallback<HTMLDivElement>;
  photoRef: RefCallback<HTMLDivElement>;
  registerCard: (id: string) => RefCallback<HTMLElement>;
  sizeOf: (id: string) => Size;
}

function elementSize(el: HTMLElement | null | undefined): Size {
  return { w: el?.offsetWidth ?? 0, h: el?.offsetHeight ?? 0 };
}

function useDeskElements(): DeskElements {
  const desk = useRef<HTMLDivElement | null>(null);
  const photo = useRef<HTMLDivElement | null>(null);
  const cards = useRef(new Map<string, HTMLElement>());
  const cardRefs = useRef(new Map<string, RefCallback<HTMLElement>>());
  const [deskNode, setDeskNode] = useState<HTMLDivElement | null>(null);
  const deskRef = useCallback((el: HTMLDivElement | null) => {
    desk.current = el;
    setDeskNode(el);
  }, []);
  const photoRef = useCallback((el: HTMLDivElement | null) => {
    photo.current = el;
  }, []);
  const registerCard = useCallback((id: string): RefCallback<HTMLElement> => {
    const cached = cardRefs.current.get(id);
    if (cached) return cached;
    const ref: RefCallback<HTMLElement> = (el) => {
      if (el) cards.current.set(id, el);
      else cards.current.delete(id);
    };
    cardRefs.current.set(id, ref);
    return ref;
  }, []);
  const sizeOf = useCallback((id: string) => elementSize(cards.current.get(id)), []);
  return { desk, photo, deskNode, deskRef, photoRef, registerCard, sizeOf };
}

function clampAllPlacements(layout: DeskLayout, width: number, sizeOf: (id: string) => Size): DeskLayout {
  const desk = { w: width, h: layout.height };
  const placements = Object.fromEntries(
    Object.entries(layout.placements).map(([id, p]) => [id, clampPlacement(p, sizeOf(id), desk)]),
  );
  return { ...layout, placements };
}

interface LayoutEngine {
  layout: DeskLayout | null;
  setLayout: Dispatch<SetStateAction<DeskLayout | null>>;
  relayout: (mode: DeskMode) => void;
  clampAll: () => void;
  userMoved: RefObject<boolean>;
  lastWidth: RefObject<number>;
}

function useLayoutEngine(el: DeskElements, itemIds: string[], leadId: string | null): LayoutEngine {
  const ids = useRef(itemIds);
  const lead = useRef(leadId);
  // Declared before useDeskLayout's relayout effect, so the refs are current when it measures.
  useLayoutEffect(() => {
    ids.current = itemIds;
    lead.current = leadId;
  });
  const userMoved = useRef(false);
  const lastWidth = useRef(0);
  const [layout, setLayout] = useState<DeskLayout | null>(null);
  const { desk, photo, sizeOf } = el;

  const measure = useCallback((): DeskMeasure | null => {
    if (!desk.current || !photo.current) return null;
    lastWidth.current = desk.current.clientWidth;
    const leadKey = lead.current !== null && ids.current.includes(lead.current) ? lead.current : null;
    return {
      width: desk.current.clientWidth,
      photo: elementSize(photo.current),
      lead: leadKey ? { id: leadKey, ...sizeOf(leadKey) } : null,
      cards: ids.current.filter((id) => id !== leadKey).map((id) => ({ id, ...sizeOf(id) })),
    };
  }, [desk, photo, sizeOf]);

  const relayout = useCallback(
    (mode: DeskMode) => {
      const m = measure();
      if (!m) return;
      userMoved.current = false;
      setLayout(mode === "tidy" ? tidyLayout(m) : scatterLayout(m));
    },
    [measure],
  );

  const clampAll = useCallback(() => {
    const width = desk.current?.clientWidth ?? 0;
    lastWidth.current = width;
    setLayout((prev) => (prev ? clampAllPlacements(prev, width, sizeOf) : prev));
  }, [desk, sizeOf]);

  return { layout, setLayout, relayout, clampAll, userMoved, lastWidth };
}

function useAnimateAfterFirstLayout(layout: DeskLayout | null): boolean {
  const [animate, setAnimate] = useState(false);
  useEffect(() => {
    if (!layout || animate) return;
    const frame = requestAnimationFrame(() => setAnimate(true));
    return () => cancelAnimationFrame(frame);
  }, [layout, animate]);
  return animate;
}

/** `leadId` is the card laid out first and centred (the search card), if any. */
export function useDeskLayout(itemIds: string[], leadId: string | null = null): DeskLayoutApi {
  const el = useDeskElements();
  const engine = useLayoutEngine(el, itemIds, leadId);
  const { layout, setLayout, relayout, clampAll, userMoved, lastWidth } = engine;
  const isStacked = useMediaQuery(DESK_STACKED_QUERY);
  const [mode, setMode] = useState<DeskMode>("scatter");
  const modeRef = useRef(mode);
  useLayoutEffect(() => {
    modeRef.current = mode;
  }, [mode]);
  const animate = useAnimateAfterFirstLayout(layout);

  // Mode changes go through toggleMode/markMoved, which handle their own layout.
  const idsKey = itemIds.join("|");
  useLayoutEffect(() => {
    if (isStacked) setLayout(null);
    else relayout(modeRef.current);
  }, [isStacked, idsKey, relayout, setLayout]);

  useRelayoutTriggers({ deskNode: el.deskNode, photo: el.photo, isStacked, modeRef, userMoved, lastWidth, relayout, clampAll });

  const toggleMode = useCallback(() => {
    const next: DeskMode = mode === "tidy" ? "scatter" : "tidy";
    setMode(next);
    relayout(next);
  }, [mode, relayout]);

  const getPlacement = useCallback((id: string) => layout?.placements[id], [layout]);
  const moveCard = useMoveCard(el, setLayout);
  const markMoved = useCallback(() => {
    userMoved.current = true;
    setMode("scatter");
  }, [userMoved]);

  const { deskRef, photoRef, registerCard } = el;
  return { deskRef, photoRef, registerCard, isStacked, mode, animate, layout, toggleMode, getPlacement, moveCard, markMoved };
}

function useMoveCard(
  el: DeskElements,
  setLayout: Dispatch<SetStateAction<DeskLayout | null>>,
): (id: string, x: number, y: number) => void {
  const { desk, sizeOf } = el;
  return useCallback(
    (id: string, x: number, y: number) => {
      const width = desk.current?.clientWidth ?? 0;
      setLayout((prev) => {
        const current = prev?.placements[id];
        if (!prev || !current) return prev;
        const next = clampPlacement({ ...current, x, y }, sizeOf(id), { w: width, h: prev.height });
        return { ...prev, placements: { ...prev.placements, [id]: next } };
      });
    },
    [desk, sizeOf, setLayout],
  );
}

interface RelayoutTriggers {
  deskNode: HTMLDivElement | null;
  photo: RefObject<HTMLDivElement | null>;
  isStacked: boolean;
  modeRef: RefObject<DeskMode>;
  userMoved: RefObject<boolean>;
  lastWidth: RefObject<number>;
  relayout: (mode: DeskMode) => void;
  clampAll: () => void;
}

/**
 * Re-measures when the desk's width changes, web fonts finish loading or the portrait loads.
 * Once the visitor has dragged something, these only nudge cards back inside the desk.
 */
function useRelayoutTriggers(t: RelayoutTriggers): void {
  const { deskNode, photo, isStacked, modeRef, userMoved, lastWidth, relayout, clampAll } = t;
  useEffect(() => {
    if (!deskNode || isStacked) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const refresh = () => {
      if (!alive) return;
      if (userMoved.current && modeRef.current === "scatter") clampAll();
      else relayout(modeRef.current);
    };
    const observer = new ResizeObserver(() => {
      if (deskNode.clientWidth === lastWidth.current) return;
      clearTimeout(timer);
      timer = setTimeout(refresh, RESIZE_DEBOUNCE_MS);
    });
    observer.observe(deskNode);
    void document.fonts.ready.then(refresh);
    const img = photo.current?.querySelector("img");
    img?.addEventListener("load", refresh);
    return () => {
      alive = false;
      clearTimeout(timer);
      observer.disconnect();
      img?.removeEventListener("load", refresh);
    };
  }, [deskNode, photo, isStacked, modeRef, userMoved, lastWidth, relayout, clampAll]);
}
